import React, { useState, useContext, useMemo, useCallback } from 'react';
import { RouteComponentProps } from '@reach/router';
import { styled } from '@mui/material/styles';
import {
  Box,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LockIcon from '@mui/icons-material/Lock';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { Button, TextView } from '@atoms';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { useArtifacts, useDeleteArtifact, materialTheme, TableContext } from '@particles';
import fetchAPI from '../../../particles/api/fetchAPI';
import { Table, BillingAccountSelector } from '@organisms';
import { columns, generateRows } from './particles';
import CreateArtifactDialog from './CreateArtifactDialog';
import ReplaceArtifactDialog from './ReplaceArtifactDialog';
import PasswordArtifactDialog from './PasswordArtifactDialog';

const StyledContainer = styled(Container)(({ theme }) => ({
  padding: theme.spacing(3)
}));

const ArtifactsList: React.FC<RouteComponentProps> = () => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedArtifact, setSelectedArtifact] = useState<Artifact| null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [replaceDialogOpen, setReplaceDialogOpen] = useState(false);
  const [artifactToReplace, setArtifactToReplace] = useState<Artifact | null>(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [artifactForPassword, setArtifactForPassword] = useState<Artifact | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [menuArtifact, setMenuArtifact] = useState<Artifact | null>(null);
  const [filterLocationCore, setFilterLocationCore] = useState<BillingAccount | null>(null);
  const [filterType, setFilterType] = useState<'all' | 'negotiator' | 'report' | 'cohort' | 'mortgage' | 'other'>('all');

  const {
    state: {
      artifactsPagination: { page, perPage, orderBy }
    }
  } = useContext(TableContext);

  const queryClient = useQueryClient();

  // Use React Query hooks with pagination - backend will default to sorting by newest first
  const { data, isLoading, error, refetch } = useArtifacts({
    page,
    pageSize: perPage,
    sortBy: orderBy,
    locationId: filterLocationCore?.locationId,
    type: filterType,
    enabled: true
  });
  const deleteArtifactMutation = useDeleteArtifact();

  const artifacts = data?.data?.data || [];
  const paginationInfo = data?.data || null;

  const handleFilterLocationSelected = (core: BillingAccount | null) => {
    setFilterLocationCore(core);
  };

  const handleFilterTypeChange = (event: { target: { value: unknown } }) => {
    setFilterType(event.target.value as 'all' | 'negotiator' | 'report' | 'cohort' | 'mortgage' | 'other');
  };

  const handleDelete = useCallback((artifact: Artifact) => {
    setSelectedArtifact(artifact);
    setDeleteDialogOpen(true);
  }, []);

  const confirmDelete = async () => {
    if (!selectedArtifact) return;

    deleteArtifactMutation.mutate(
      { id: selectedArtifact.id },
      {
        onSuccess: () => {
          toast.success('Artifact deleted successfully');
          setDeleteDialogOpen(false);
          setSelectedArtifact(null);
        },
        onError: (error: unknown) => {
          const errorMessage = error && typeof error === 'object' && 'error' in error 
            ? String(error.error) 
            : 'Unknown error';
          toast.error(`Error deleting artifact: ${errorMessage}`);
        }
      }
    );
  };

  const handleBulkDelete = useCallback(async (selectedRows: TableRow[]) => {
    if (selectedRows.length === 0) return;

    // Filter out in-progress job rows (their IDs start with "job-")
    const selectedIds = selectedRows
      .map((row) => row.id as string)
      .filter((id) => !id.startsWith('job-'));
    
    // Show loading toast
    const loadingToast = toast.info(`Deleting ${selectedIds.length} artifact(s)...`, {
      autoClose: false
    });

    // Delete all selected artifacts in parallel using fetchAPI
    const deletePromises = selectedIds.map(async (id) => {
      try {
        const response = await fetchAPI({
          method: 'DELETE',
          route: `artifacts/${id}`
        });
        
        if (response.code === 200 || response.code === 204) {
          return { id, success: true };
        } else {
          return { 
            id, 
            success: false, 
            error: (response.error as { message?: string })?.message || 'Failed to delete' 
          };
        }
      } catch (error) {
        return { 
          id, 
          success: false, 
          error: error instanceof Error ? error.message : 'Unknown error' 
        };
      }
    });

    const results = await Promise.all(deletePromises);
    
    // Dismiss loading toast
    toast.dismiss(loadingToast);

    const successCount = results.filter((r) => r.success).length;
    const failCount = results.filter((r) => !r.success).length;

    // Invalidate queries to refresh the list
    if (successCount > 0) {
      queryClient.invalidateQueries({ queryKey: ['artifacts'] });
    }

    // Show summary toast
    if (successCount > 0) {
      toast.success(`Successfully deleted ${successCount} artifact(s)`);
    }
    if (failCount > 0) {
      toast.error(`Failed to delete ${failCount} artifact(s)`);
    }
  }, [queryClient]);

  const handleCopyLink = useCallback((artifact: Artifact) => {
    navigator.clipboard.writeText(`${process.env.REACT_APP_API_BASE}/a/${artifact.fileName}`);
    toast.success('Share link copied to clipboard');
  }, []);

  const handleViewArtifact = useCallback((artifact: Artifact) => {
    window.open(`${process.env.REACT_APP_API_BASE}/a/${artifact.fileName}`, '_blank');
  }, []);

  const handleReplace = useCallback((artifact: Artifact) => {
    setArtifactToReplace(artifact);
    setReplaceDialogOpen(true);
  }, []);

  const handlePassword = useCallback((artifact: Artifact) => {
    setArtifactForPassword(artifact);
    setPasswordDialogOpen(true);
  }, []);

  const handleMenuOpen = useCallback((artifact: Artifact, event: React.MouseEvent<HTMLElement>) => {
    setMenuArtifact(artifact);
    setMenuAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
    setMenuArtifact(null);
  }, []);

  const runMenuAction = useCallback((action: (artifact: Artifact) => void) => {
    if (menuArtifact) action(menuArtifact);
    handleMenuClose();
  }, [menuArtifact, handleMenuClose]);

  const rows = useMemo(() => {
    return generateRows(artifacts, {
      onMenuOpen: handleMenuOpen
    });
  }, [artifacts, handleMenuOpen]);

  const pagination: Pagination = paginationInfo ? {
    data: artifacts,
    other: null,
    message: paginationInfo.message || '',
    pageNumber: paginationInfo.pageNumber || page,
    pageSize: paginationInfo.pageSize || perPage,
    pageCount: paginationInfo.pageCount || 0,
    totalPageCount: paginationInfo.totalPageCount || 1,
    totalCount: paginationInfo.totalCount || 0
  } : {
    data: [],
    other: null,
    message: '',
    pageNumber: 1,
    pageSize: perPage,
    pageCount: 0,
    totalPageCount: 1,
    totalCount: 0
  };

  return (
    <StyledContainer maxWidth="xl">
      <Box sx={{ mb: 3 }}>
        {/* Filters Section */}
        <Box sx={{ mb: 3 }}>
          <Stack
            direction="row"
            spacing={3}
            useFlexGap
            sx={{ mb: 2, flexWrap: 'wrap', rowGap: 2, alignItems: 'center' }}
          >
            <Box>
              <BillingAccountSelector
                type="c"
                status="both"
                caller="artifacts-filter"
                title="Filter by Account"
                onSelected={handleFilterLocationSelected}
              />
            </Box>
            <Box>
              <FormControl fullWidth>
                <InputLabel id="filter-type-label">Type</InputLabel>
                <Select
                  labelId="filter-type-label"
                  id="filter-type-select"
                  value={filterType}
                  size="small"
                  label="Type"
                  onChange={handleFilterTypeChange}
                  sx={{ minHeight: '40px' }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="negotiator">Negotiator</MenuItem>
                  <MenuItem value="report">Core</MenuItem>
                  <MenuItem value="cohort">Cohort</MenuItem>
                  <MenuItem value="mortgage">Mortgage</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }} />
            <Tooltip title="Refresh">
              <IconButton onClick={() => refetch()} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{ height: '45px' }}>
            Create Artifact
          </Button>
          </Stack>
        </Box>

      </Box>

      <Table
        name="artifacts"
        columns={columns}
        data={rows}
        pagination={pagination}
        isLoading={isLoading}
        error={error ? new Error('Failed to fetch artifacts') : null}
        selectable={true}
        onBulkDelete={handleBulkDelete}
        bulkDeleteLabel="Delete Selected"
        bulkDeleteDialogTitle="Delete Multiple Artifacts"
        getRowDisplayText={(row) => {
          const title = typeof row.title === 'string' ? row.title : 'Untitled';
          return title;
        }}
        persistColumnPrefs
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DeleteIcon sx={{ mr: 1, color: materialTheme.palette.error.main }} />
            <TextView variant="h6" $fontWeight={600}>
              Delete Artifact
            </TextView>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <TextView variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete this artifact?
          </TextView>
          {selectedArtifact && (
            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <TextView $fontWeight={600} sx={{ mb: 1 }}>
                {selectedArtifact.title}
              </TextView>
              <TextView variant="body2" color="textSecondary">
                {selectedArtifact.description}
              </TextView>
            </Box>
          )}
          <TextView variant="body2" color="error" sx={{ mt: 2 }}>
            This action cannot be undone.
          </TextView>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            variant="outlined" 
            onClick={() => setDeleteDialogOpen(false)} 
            disabled={deleteArtifactMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmDelete}
            loading={deleteArtifactMutation.isPending}
            startIcon={<DeleteIcon htmlColor="white" />}
            sx={{ 
              bgcolor: 'error.main', 
              color: 'white',
              '&:hover': { bgcolor: 'error.dark' }
            }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create artifact (Core / Negotiator / Custom report) */}
      <CreateArtifactDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />

      {/* Replace HTML for an existing artifact (preserves the share link) */}
      <ReplaceArtifactDialog
        open={replaceDialogOpen}
        artifact={artifactToReplace}
        onClose={() => {
          setReplaceDialogOpen(false);
          setArtifactToReplace(null);
        }}
      />

      {/* Set / change / remove the share-link password */}
      <PasswordArtifactDialog
        open={passwordDialogOpen}
        artifact={artifactForPassword}
        onClose={() => {
          setPasswordDialogOpen(false);
          setArtifactForPassword(null);
        }}
      />

      {/* Row-level kebab menu: collapses View / Copy / Replace / Password / Delete */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MenuItem onClick={() => runMenuAction(handleViewArtifact)}>
          <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => runMenuAction(handleCopyLink)}>
          <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Copy link</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => runMenuAction(handleReplace)}>
          <ListItemIcon><UploadFileIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Replace HTML</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => runMenuAction(handlePassword)}>
          <ListItemIcon><LockIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Password</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => runMenuAction(handleDelete)} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </StyledContainer>
  );
};

export default ArtifactsList;
